import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { notificationAPI, chatAPI, adminAPI } from '../services/api';
import { chatService } from '../services/chatService';
import FeedbackModal from './FeedbackModal';
import { NotificationSkeleton, EmptyState } from './skeletons';
import {
  LayoutDashboard,
  Search,
  Send,
  MessageSquare,
  Filter,
  Layers,
  BarChart3,
  MapPin,
  Sliders,
  SlidersHorizontal,
  KeyRound,
  PackageSearch,
  UserPlus,
  Megaphone,
  Images,
  Wallet,
  FileText,
  ShieldCheck,
  Bell,
  User,
  Mail,
  Target,
  Home,
  PlusCircle,
  FolderOpen,
  Lock,
  ClipboardList,
  GitBranch,
  DollarSign,
  Tag,
  Store,
  Percent,
  Settings,
  PartyPopper,
  XCircle,
  Package,
  CheckSquare,
  AlertOctagon,
  Coins,
  AlertTriangle,
  TrendingDown,
  Eye,
  CreditCard
} from 'lucide-react';

const NAVS = {
  buyer: [
    { path: '/buyer/home', icon: <Home size={18} />, label: 'Home' },
    { path: '/buyer/post-demand', icon: <PlusCircle size={18} />, label: 'Post Demand' },
    { path: '/buyer/demands', icon: <FolderOpen size={18} />, label: 'My Demands' },
    { path: '/buyer/bulk', icon: <Package size={18} />, label: 'Bulk Sales' },
    { path: '/buyer/messages', icon: <MessageSquare size={18} />, label: 'Messages', isMessages: true },
    { path: '/buyer/notifications', icon: <Bell size={18} />, label: 'Notifications', isNotification: true },
    { path: '/buyer/account', icon: <User size={18} />, label: 'My Account' },
    { path: '/buyer/security', icon: <Lock size={18} />, label: 'Change Password' },
    { path: '/buyer/issues', icon: <AlertOctagon size={18} />, label: 'Report Issue' },
  ],
  vendor: [
    { path: '/vendor/dashboard', icon: <LayoutDashboard size={18} />, label: 'Dashboard' },
    { path: '/vendor/demands', icon: <Search size={18} />, label: 'Browse Demands' },
    { path: '/vendor/offers', icon: <Send size={18} />, label: 'My Offers' },
    { path: '/vendor/messages', icon: <MessageSquare size={18} />, label: 'Messages', isMessages: true },
    { path: '/vendor/filters', icon: <Filter size={18} />, label: 'Lead Filters' },
    { path: '/vendor/bulk', icon: <Layers size={18} />, label: 'Bulk Sales' },
    { path: '/vendor/analytics', icon: <BarChart3 size={18} />, label: 'Analytics' },
    { path: '/vendor/wallet', icon: <Wallet size={18} />, label: 'Wallet' },
    { path: '/vendor/invoices', icon: <FileText size={18} />, label: 'Invoices' },
    { path: '/vendor/kyc', icon: <ShieldCheck size={18} />, label: 'KYC' },
    { path: '/vendor/notifications', icon: <Bell size={18} />, label: 'Notifications', isNotification: true },
    { path: '/vendor/account', icon: <User size={18} />, label: 'My Account' },
    { path: '/vendor/issues', icon: <AlertOctagon size={18} />, label: 'Report Issue' },
  ],
  admin: [
    { path: '/admin/dashboard', icon: <LayoutDashboard size={18} />, label: 'Dashboard' },
    { path: '/admin/taxonomy', icon: <GitBranch size={18} />, label: 'Taxonomy' },
    { path: '/admin/pricing', icon: <DollarSign size={18} />, label: 'Lead Pricing' },
    { path: '/admin/items', icon: <Tag size={18} />, label: 'Manage Items' },
    { path: '/admin/catalog-images', icon: <Images size={18} />, label: 'Catalog Images' },
    { path: '/admin/ai-setup', icon: <KeyRound size={18} />, label: 'AI Setup' },
    { path: '/admin/catalog-attributes', icon: <SlidersHorizontal size={18} />, label: 'Catalog Attributes' },
    { path: '/admin/discovery', icon: <PackageSearch size={18} />, label: 'Product Discovery' },
    { path: '/admin/vendor-discovery', icon: <UserPlus size={18} />, label: 'Vendor Discovery' },
    { path: '/admin/vendor-outreach', icon: <Megaphone size={18} />, label: 'Vendor Outreach' },
    { path: '/admin/vendors', icon: <Store size={18} />, label: 'Vendors' },
    { path: '/admin/kyc', icon: <ShieldCheck size={18} />, label: 'KYC Review' },
    { path: '/admin/demands', icon: <ClipboardList size={18} />, label: 'Demands' },
    { path: '/admin/wallets', icon: <Wallet size={18} />, label: 'Wallets' },
    { path: '/admin/invoices', icon: <FileText size={18} />, label: 'Invoices' },
    { path: '/admin/gst-rules', icon: <Percent size={18} />, label: 'GST Rules' },
    { path: '/admin/analytics', icon: <BarChart3 size={18} />, label: 'Analytics' },
    { path: '/admin/geo-analytics', icon: <MapPin size={18} />, label: 'Geo Analytics' },
    { path: '/admin/market-profiles', icon: <Sliders size={18} />, label: 'Market Profiles' },
    { path: '/admin/email-templates', icon: <Mail size={18} />, label: 'Email Templates' },
    { path: '/admin/settings', icon: <Settings size={18} />, label: 'System Settings' },
    { path: '/admin/feedback', icon: <MessageSquare size={18} />, label: 'User Feedbacks' },
    { path: '/admin/issues', icon: <AlertOctagon size={18} />, label: 'System Issues' },
    { path: '/admin/credit-requests', icon: <CreditCard size={18} />, label: 'Credit Requests' },
    { path: '/admin/notifications', icon: <Bell size={18} />, label: 'Notifications', isNotification: true },
    { path: '/admin/profile', icon: <User size={18} />, label: 'Profile' },
  ],
};

const BG = { buyer: 'var(--green)', vendor: '#1F2937', admin: '#4C1D95' };

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { unreadCount, fetchUnread } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [notificationsList, setNotificationsList] = useState([]);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const [unreadChatsCount, setUnreadChatsCount] = useState(0);
  const [systemMode, setSystemMode] = useState('live_production');

  const fetchSystemMode = useCallback(async () => {
    if (user && user.role === 'admin') {
      try {
        const res = await adminAPI.getSettings();
        const modeSetting = (res.data.data || []).find(s => s.key === 'system_mode');
        if (modeSetting) {
          setSystemMode(modeSetting.value);
        }
      } catch (err) {
        console.error('Failed to load system mode in AppLayout:', err);
      }
    }
  }, [user]);

  useEffect(() => {
    fetchSystemMode();
  }, [fetchSystemMode, location.pathname]);

  const dropdownRef = useRef(null);
  const role = user?.role || 'buyer';
  let items = NAVS[role] || NAVS.buyer;
  if (role === 'vendor' && user?.vendorType === 'financial') {
    items = items.filter(item => item.path !== '/vendor/offers' && item.path !== '/vendor/bulk');
  }
  if (role === 'admin') {
    if (systemMode !== 'local_production') {
      items = items.filter(item => item.path !== '/admin/credit-requests');
    }
  }

  // Load short list of notifications when dropdown opens
  const fetchRecentNotifications = useCallback(async () => {
    try {
      setLoadingNotifs(true);
      const res = await notificationAPI.list({ page: 1, limit: 5 });
      if (res.data.success) {
        setNotificationsList(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch recent notifications:', err);
    } finally {
      setLoadingNotifs(false);
    }
  }, []);

  useEffect(() => {
    if (notifDropdownOpen) {
      fetchRecentNotifications();
    }
  }, [notifDropdownOpen, fetchRecentNotifications]);

  const fetchUnreadChatsCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await chatAPI.rooms();
      const count = (res.data.data || []).filter(r => (r.unreadCount || 0) > 0).length;
      setUnreadChatsCount(count);
    } catch (err) {
      console.error('Failed to fetch unread chats count:', err);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchUnreadChatsCount();

      // Ensure global singleton socket connection is initialized
      chatService.connect(null, user._id);

      // Listen for message events globally to update the sidebar unread count
      const unsubscribeMessage = chatService.onMessage((newMsg) => {
        if (String(newMsg.senderId) !== String(user._id)) {
          fetchUnreadChatsCount();
        }
      });

      const unsubscribeMarkRead = chatService.onMarkRead(() => {
        fetchUnreadChatsCount();
      });

      // Poll as a fallback every 30 seconds
      const timer = setInterval(fetchUnreadChatsCount, 30000);

      return () => {
        unsubscribeMessage();
        unsubscribeMarkRead();
        clearInterval(timer);
      };
    } else {
      setUnreadChatsCount(0);
    }
  }, [user, fetchUnreadChatsCount]);

  // Click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setNotifDropdownOpen(false);
        setProfileDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAsRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotificationsList(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
      fetchUnread();
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotificationsList(prev => prev.map(n => ({ ...n, isRead: true })));
      fetchUnread();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleNotifClick = (notif) => {
    setNotifDropdownOpen(false);
    if (!notif.isRead) handleMarkAsRead(notif._id);

    if (notif.type === 'offer_received') {
      navigate(`/buyer/demands/${notif.data.demandId?._id || notif.data.demandId}`);
    } else if (notif.type === 'lead_received') {
      navigate(`/vendor/demands`);
    } else if (notif.type === 'offer_accepted' || notif.type === 'offer_rejected') {
      navigate(`/vendor/offers`);
    } else if (notif.type === 'bulk_bid') {
      navigate(`/vendor/bulk`);
    } else if (notif.type === 'bulk_accepted' || notif.type === 'bulk_rejected') {
      navigate(`/buyer/bulk`);
    } else if (notif.type === 'wallet_recharge' || notif.type === 'wallet_low' || notif.type === 'lead_skipped') {
      navigate(`/vendor/wallet`);
    } else if (notif.type === 'kyc_status') {
      navigate(`/vendor/kyc`);
    } else if (notif.type === 'admin_kyc_review') {
      navigate(`/admin/kyc`);
    } else if (notif.type === 'admin_demand_review') {
      navigate(`/admin/demands`);
    } else if (notif.type === 'admin_platform_fee') {
      navigate(`/admin/vendors`);
    } else if (notif.type === 'admin_credit_request') {
      navigate(`/admin/credit-requests`);
    } else if (notif.type === 'wallet_credit_added') {
      navigate(`/vendor/wallet`);
    }
  };

  const getNotifIcon = (type) => {
    const size = 16;
    switch (type) {
      case 'offer_received': return <Mail size={size} color="#3B82F6" />;
      case 'lead_received': return <Target size={size} color="#1B5C3A" />;
      case 'offer_accepted': return <PartyPopper size={size} color="#1B5C3A" />;
      case 'offer_rejected': return <XCircle size={size} color="#EF4444" />;
      case 'bulk_bid': return <Package size={size} color="#3B82F6" />;
      case 'bulk_accepted': return <CheckSquare size={size} color="#1B5C3A" />;
      case 'bulk_rejected': return <AlertOctagon size={size} color="#EF4444" />;
      case 'wallet_recharge': return <Coins size={size} color="#1B5C3A" />;
      case 'wallet_low': return <AlertTriangle size={size} color="#F5A623" />;
      case 'lead_skipped': return <TrendingDown size={size} color="#EF4444" />;
      case 'kyc_status': return <ShieldCheck size={size} color="#1B5C3A" />;
      case 'admin_kyc_review': return <Eye size={size} color="#3B82F6" />;
      case 'admin_demand_review': return <FileText size={size} color="#3B82F6" />;
      case 'admin_platform_fee': return <CreditCard size={size} color="#F5A623" />;
      case 'admin_credit_request': return <CreditCard size={size} color="#3B82F6" />;
      case 'wallet_credit_added': return <Coins size={size} color="#1B5C3A" />;
      default: return <Bell size={size} color="#6B7280" />;
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      minHeight: '100vh', 
      height: location.pathname.endsWith('/messages') ? '100vh' : 'auto',
      overflow: location.pathname.endsWith('/messages') ? 'hidden' : 'visible'
    }}>
      {/* Sidebar Overlay for Mobile */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, backdropFilter: 'blur(2px)' }}
        />
      )}

      {/* Sidebar */}
      <aside style={{
        width: 240, flexShrink: 0, background: BG[role], display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, left: sidebarOpen ? 0 : -240, bottom: 0, zIndex: 1001,
        transition: 'left 0.3s ease',
        boxShadow: sidebarOpen ? '10px 0 30px rgba(0,0,0,0.2)' : 'none'
      }}>
        <div style={{ padding: '20px 18px', borderBottom: '1px solid rgba(255,255,255,.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img
              src={role === 'vendor' ? '/logo-vendor.png' : '/logo-1.png'}
              alt="Kanjusee"
              style={{
                height: 36,
                width: 36,
                objectFit: 'contain',
                borderRadius: 8,
                flexShrink: 0
              }}
            />
            <div>
              <div style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 900, color: '#fff', lineHeight: 1 }}>
                Kanjusee<span style={{ color: 'var(--gold)' }}>.</span>
              </div>
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: 10, color: 'rgba(255,255,255,.4)', marginTop: 3, textTransform: 'uppercase', letterSpacing: 1 }}>
                {role === 'buyer' ? 'Buyer App' : role === 'vendor' ? 'Vendor App' : 'Admin Panel'}
              </div>
            </div>
          </div>
          <button className="show-mobile" onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}>
            ✕
          </button>
        </div>

        <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
          {items.map(n => (
            <NavLink key={n.path} to={n.path}
              onClick={() => setSidebarOpen(false)}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', cursor: 'pointer', textDecoration: 'none',
                background: isActive ? 'rgba(255,255,255,.1)' : 'transparent',
                borderLeft: isActive ? '4px solid var(--gold)' : '4px solid transparent',
                transition: 'all .15s',
              })}
            >
              {({ isActive }) => (
                <>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                    fontSize: 18,
                    flexShrink: 0,
                    color: isActive ? '#fff' : 'rgba(255,255,255,.55)',
                    transition: 'color 0.15s'
                  }}>
                    {n.icon}
                  </span>
                  <span style={{ fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: isActive ? 700 : 500, color: isActive ? '#fff' : 'rgba(255,255,255,.5)', flex: 1 }}>{n.label}</span>
                  {n.isNotification && unreadCount > 0 && (
                    <span style={{
                      background: 'var(--red)', color: '#fff', fontSize: 10, fontWeight: 800,
                      padding: '2px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center'
                    }}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                  {n.isMessages && unreadChatsCount > 0 && (
                    <span style={{
                      background: '#25D366', color: '#fff', fontSize: 10, fontWeight: 800,
                      padding: '2px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center',
                      boxShadow: '0 2px 4px rgba(37,211,102,0.3)'
                    }}>
                      {unreadChatsCount > 99 ? '99+' : unreadChatsCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,.1)' }}>
          <div style={{ fontSize: 13, color: '#fff', fontWeight: 600, marginBottom: 4 }}>{user?.fullName}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', marginBottom: 12 }}>{user?.email}</div>
          <button onClick={() => { logout(); navigate('/login'); }}
            style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,.1)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'background 0.2s' }}>
            Sign Out
          </button>
        </div>
      </aside>

      {/* Adjust Main Content to not be hidden behind fixed sidebar on desktop */}
      <div style={{ display: 'none' }} className="hide-mobile">
        <style>{`
          @media (min-width: 769px) {
            aside { 
              position: sticky !important; 
              top: 0 !important; 
              height: 100vh !important; 
              left: 0 !important; 
            }
            .sidebar-toggle { display: none !important; }
          }
        `}</style>
      </div>

      {/* Main */}
      <div style={{ flex: 1, background: 'var(--cream)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ height: 60, background: 'var(--white)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', padding: '0 20px', position: 'sticky', top: 0, zIndex: 50 }}>
          <button
            className="show-mobile sidebar-toggle"
            onClick={() => setSidebarOpen(true)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: 16, padding: 4 }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--char)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <div style={{ flex: 1, fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 700, color: 'var(--char)' }}>
            {items.find(n => window.location.pathname.startsWith(n.path))?.label || 'Dashboard'}
          </div>

          {/* Top-Right Area: Notification Bell & Avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, position: 'relative' }} ref={dropdownRef}>
            {/* Bell Icon Trigger */}
            <button
              onClick={() => setNotifDropdownOpen(!notifDropdownOpen)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', position: 'relative',
                padding: '6px', borderRadius: '50%', transition: 'background-color 0.2s',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--char)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
              {unreadCount > 0 && (
                <span style={{
                  position: 'absolute', top: 2, right: 2, background: 'var(--red)', color: '#fff',
                  fontSize: 10, fontWeight: 800, height: 16, minWidth: 16, borderRadius: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
                  boxShadow: '0 0 0 2px var(--white)', animation: 'pulse 2s infinite'
                }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notification Dropdown Popover */}
            {notifDropdownOpen && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: 10, width: 340,
                backgroundColor: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)',
                boxShadow: 'var(--shadow-lg)', zIndex: 1000, overflow: 'hidden'
              }}>
                {/* Header */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 16px', borderBottom: '1px solid var(--border)', backgroundColor: '#f9fafb'
                }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--char)' }}>Recent Notifications</span>
                  {notificationsList.some(n => !n.isRead) && (
                    <button
                      onClick={handleMarkAllAsRead}
                      style={{
                        background: 'none', border: 'none', color: 'var(--green)',
                        fontSize: 12, fontWeight: 700, cursor: 'pointer'
                      }}
                    >
                      Mark all as read
                    </button>
                  )}
                </div>

                {/* List Container */}
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {loadingNotifs ? (
                    <NotificationSkeleton count={3} style={{ padding: '12px 16px' }} />
                  ) : notificationsList.length === 0 ? (
                    <EmptyState
                      title="No notifications yet"
                      description="We'll notify you when something important happens."
                      style={{ border: 'none', padding: '24px 16px', margin: 0 }}
                    />
                  ) : (
                    notificationsList.map(notif => (
                      <div
                        key={notif._id}
                        onClick={() => handleNotifClick(notif)}
                        style={{
                          display: 'flex', gap: 12, padding: '12px 16px', cursor: 'pointer',
                          borderBottom: '1px solid #f3f4f6', transition: 'background-color 0.2s',
                          backgroundColor: notif.isRead ? 'transparent' : 'rgba(27, 92, 58, 0.04)'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = notif.isRead ? '#f9fafb' : 'rgba(27, 92, 58, 0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = notif.isRead ? 'transparent' : 'rgba(27, 92, 58, 0.04)'}
                      >
                        <div style={{
                          fontSize: 18, width: 32, height: 32, borderRadius: '50%',
                          backgroundColor: notif.isRead ? '#f3f4f6' : 'rgba(27, 92, 58, 0.1)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                          {getNotifIcon(notif.type)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--char)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {notif.title}
                            </div>
                            <span style={{ fontSize: 10, color: 'var(--muted)', flexShrink: 0, marginLeft: 6 }}>
                              {new Date(notif.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {notif.message}
                          </div>
                        </div>
                        {!notif.isRead && (
                          <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'var(--green)', alignSelf: 'center', flexShrink: 0 }} />
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* Footer */}
                <div style={{ borderTop: '1px solid var(--border)', textAlign: 'center' }}>
                  <button
                    onClick={() => {
                      setNotifDropdownOpen(false);
                      navigate(`/${role}/notifications`);
                    }}
                    style={{
                      width: '100%', padding: '10px', background: 'none', border: 'none',
                      color: 'var(--green)', fontWeight: 700, fontSize: 12, cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    View All Notifications
                  </button>
                </div>
              </div>
            )}

            {/* Profile Avatar */}
            <button
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'var(--green-lt)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 15,
                fontWeight: 700,
                color: 'var(--green)',
                border: '2px solid var(--white)',
                boxShadow: 'var(--shadow-sm)',
                cursor: 'pointer',
                padding: 0
              }}
            >
              {user?.fullName?.charAt(0) || 'U'}
            </button>

            {/* Profile Dropdown Popover */}
            {profileDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: 10,
                width: 220,
                backgroundColor: 'var(--white)',
                borderRadius: 12,
                border: '1px solid var(--border)',
                boxShadow: 'var(--shadow-lg)',
                zIndex: 1000,
                overflow: 'hidden',
                padding: '8px 0',
                animation: 'fadeIn 0.15s ease-out'
              }}>
                {/* Header */}
                <div style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--border)',
                  marginBottom: '6px'
                }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--char)' }}>{user?.fullName}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.email}</div>
                </div>

                {/* Option: My Profile */}
                <button
                  onClick={() => {
                    setProfileDropdownOpen(false);
                    if (role === 'admin') {
                      navigate('/admin/profile');
                    } else {
                      navigate(`/${role}/account`);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: 'none',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    color: 'var(--char)',
                    fontSize: '13px',
                    fontWeight: 600,
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{ fontSize: 16 }}>👤</span>
                  My Profile
                </button>

                {/* Option: Sign Out */}
                <button
                  onClick={() => {
                    setProfileDropdownOpen(false);
                    logout();
                    navigate(role === 'vendor' ? '/vendor/login' : role === 'admin' ? '/admin/login' : '/login');
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: 'none',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    color: '#DC2626',
                    fontSize: '13px',
                    fontWeight: 600,
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{ fontSize: 16 }}>🚪</span>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
        <div 
          style={{ 
            flex: 1, 
            padding: location.pathname.endsWith('/messages') ? '0' : '20px', 
            overflowY: location.pathname.endsWith('/messages') ? 'hidden' : 'auto',
            display: location.pathname.endsWith('/messages') ? 'flex' : 'block',
            flexDirection: location.pathname.endsWith('/messages') ? 'column' : 'initial',
            maxWidth: location.pathname.endsWith('/messages') ? 'none' : undefined,
            margin: location.pathname.endsWith('/messages') ? '0' : undefined
          }} 
          className={location.pathname.endsWith('/messages') ? 'message-container' : 'container'}
        >
          <Outlet />
        </div>
      </div>
      {user && (user.role === 'buyer' || user.role === 'vendor') && (
        <FeedbackModal user={user} />
      )}
      <style>{`
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
          70% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
          100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }
      `}</style>
    </div>
  );
}
